"use client";

// Buyer-side video embed on the deal detail page (BLOK4 P0 #4, MEDIA "koper-
// embed"). Shows at most 2 of the deal's published "Cinematic" clips from the
// creator media library, honestly labeled as AI-generated impressions (see
// src/lib/content/deal-video-copy.js for the hard honesty line).
//
// PERFORMANCE CONTRACT (skeleton-budget rule, memory
// feedback_skeleton_budget_5s): this component adds ZERO fetches to the
// render-critical path. The clip list is a static import resolved in page.js
// (compiled into the bundle by the MEDIA-2 mapping generator, no BFF call),
// and every <video> uses preload="none" so the stream is only requested when
// the visitor presses play. The poster is a plain <img>-style fetch of a
// CF-cached thumbnail, below the fold, non-blocking.
//
// Deals WITHOUT clips: page.js only renders this component when the mapping
// has clips, and this component double-guards by returning null — so a deal
// without clips gets byte-identical page output (no empty section).
//
// TRACKING: first play of each clip beacons { dealId, fileId } to the library
// ingest (video_view_events), giving the per-deal view signal the flywheel
// step 3 needs. Best-effort: a blocked/failed beacon never affects playback.

import { useRef } from "react";
import { getLibraryTrackViewUrl } from "@/lib/deal-library-links";

const MAX_CLIPS = 2;

function sendPlayBeacon(dealId, fileId) {
  try {
    const url = getLibraryTrackViewUrl();
    const payload = JSON.stringify({
      dealId: String(dealId),
      fileId: String(fileId),
      source: "deal-page",
    });
    if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
      navigator.sendBeacon(url, payload);
      return;
    }
    if (typeof fetch === "function") {
      fetch(url, {
        method: "POST",
        body: payload,
        keepalive: true,
        // Opaque response is fine — fire-and-forget, we never read it.
        mode: "no-cors",
      }).catch(() => {});
    }
  } catch {
    // Tracking must never surface as a player error.
  }
}

export default function DealVideoSection({ dealId, clips, copy }) {
  // One beacon per clip per page view, also under React strict-mode replays.
  const firedRef = useRef(null);
  if (firedRef.current === null) firedRef.current = new Set();

  if (!Array.isArray(clips) || clips.length === 0 || !copy) return null;
  const shown = clips.slice(0, MAX_CLIPS).filter((c) => c && c.fileId && c.streamUrl);
  if (shown.length === 0) return null;

  const handlePlay = (fileId) => {
    if (firedRef.current.has(fileId)) return;
    firedRef.current.add(fileId);
    sendPlayBeacon(dealId, fileId);
  };

  return (
    <div className="max-w-[1320px] mx-auto px-4 mb-6" data-testid="deal-video-section">
      <div className="bg-white border border-gray-200 rounded-lg p-6 lg:p-8">
        <div className="flex items-center gap-2 mb-1">
          <span
            className="inline-flex items-center gap-1.5 text-[13px] font-semibold px-2.5 py-1 rounded-full"
            style={{ background: "#E8F4FE", color: "#0770C0" }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polygon points="23 7 16 12 23 17 23 7" />
              <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
            </svg>
            {copy.eyebrow}
          </span>
        </div>
        <h2 className="text-xl font-bold text-gray-900 mb-2">{copy.title}</h2>
        <p className="text-sm text-gray-500 mb-4">{copy.note}</p>
        <div className="flex flex-wrap gap-4">
          {shown.map((clip) => (
            <video
              key={clip.fileId}
              src={clip.streamUrl}
              poster={clip.posterUrl}
              controls
              playsInline
              preload="none"
              aria-label={copy.clipLabel}
              className="rounded-lg bg-gray-100 w-full max-w-[260px] aspect-[9/16] object-cover"
              onPlay={() => handlePlay(clip.fileId)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
